const DEFAULT_TTL = 3 * 60 * 60 * 1000; // 3 jam
const CACHE_PREFIX = "ref_cache_";

interface CacheItem<T = any> {
  data: T;
  expiry: number;
}

export const getCache = <T = any>(key: string): T | null => {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const cached: CacheItem<T> = JSON.parse(raw);
    if (Date.now() > cached.expiry) {
      localStorage.removeItem(CACHE_PREFIX + key);
      return null;
    }
    return cached.data;
  } catch {
    localStorage.removeItem(CACHE_PREFIX + key);
    return null;
  }
};

export const setCache = <T = any>(
  key: string,
  data: T,
  ttl: number = DEFAULT_TTL,
): void => {
  try {
    const item: CacheItem<T> = { data, expiry: Date.now() + ttl };
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(item));
  } catch (e) {
    console.warn("Cache write failed:", e);
  }
};

export const removeCache = (key: string): void => {
  localStorage.removeItem(CACHE_PREFIX + key);
};

export const removeCacheByPrefix = (prefix: string): void => {
  const fullPrefix = CACHE_PREFIX + prefix;
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(fullPrefix)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
};

export const clearAllCache = (): void => {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(CACHE_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
};

export const buildCacheKey = (
  endpoint: string,
  params?: Record<string, any>,
): string => {
  if (!params || Object.keys(params).length === 0) return endpoint;
  const sortedParams = Object.keys(params)
    .sort((a, b) => a.localeCompare(b))
    .filter((k) => params[k] !== undefined && params[k] !== null)
    .map((k) => `${k}=${params[k]}`)
    .join("&");
  return sortedParams ? `${endpoint}_${sortedParams}` : endpoint;
};
